import { create } from "zustand";
import { client } from "@/lib/api";
import { readChatEvents } from "@/lib/frames";
import { useOrgSocket } from "@/lib/socket";
import { holdLocalChat } from "@/lib/dataSource";
import { setRetentionGuard, useChatStore } from "./chatStore";
import { readCachedChat } from "./chatPersistence";
import { persistentStore, persistenceUserId, userPrefix } from "@/lib/persistence";

/**
 * Chat subscriptions, reference-counted.
 *
 * The open chat holds one, and so does every chat with a run going in the
 * selected worktree (so switching to it needs no replay). The subscription
 * lives while anything holds it; a release that is re-held in the same tick
 * (switching which chat is in front) keeps it, with no unsubscribe/resubscribe.
 *
 * Opening order, fastest paint first, and nothing downloaded twice:
 *  1. memory — a chat seen this page session is already folded;
 *  2. this browser's cache — the last 20 chats' events and their cursor;
 *     the subscribe resumes from that cursor;
 *  3. the relay — the `tailTurns` page paints first, and the subscribe then
 *     resumes from its `coverage.throughSeq`, so the replay carries only what
 *     happened since. Older history is paged on scroll-up.
 */

/** Turns in the first page of a cold chat, and in each older page. */
export const TAIL_TURNS = 10;
export const OLDER_PAGE_TURNS = 20;

const holds = new Map<string, number>();
/** Bumped on every fresh start and teardown, so a start whose awaits outlived
 * its hold does nothing. */
const generations = new Map<string, number>();
/** Released to zero this tick; torn down in a microtask unless re-held. */
const pendingRelease = new Set<string>();
const localReleases = new Map<string, () => void>();
const inflightTail = new Map<string, Promise<void>>();
const inflightOlder = new Map<string, Promise<void>>();
setRetentionGuard((chatId) => holds.has(chatId) || pendingRelease.has(chatId));

export interface HoldOptions {
  /** The worktree, when known, so a same-machine worker's provisional tail
   * can follow the subscription. */
  worktreeId?: string;
}

function bump(chatId: string): number {
  const next = (generations.get(chatId) ?? 0) + 1;
  generations.set(chatId, next);
  return next;
}

export function holdChatSubscription(chatId: string, options: HoldOptions = {}): () => void {
  const count = holds.get(chatId) ?? 0;
  holds.set(chatId, count + 1);
  if (count === 0) {
    if (pendingRelease.delete(chatId)) {
      // Re-held before the teardown ran: still subscribed, nothing to do.
    } else {
      void start(chatId, bump(chatId), options);
    }
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const remaining = (holds.get(chatId) ?? 1) - 1;
    if (remaining > 0) {
      holds.set(chatId, remaining);
      return;
    }
    holds.delete(chatId);
    pendingRelease.add(chatId);
    queueMicrotask(() => {
      if (!pendingRelease.delete(chatId)) return;
      bump(chatId);
      localReleases.get(chatId)?.();
      localReleases.delete(chatId);
      useOrgSocket.getState().unsubscribeChat(chatId);
      // Provisionals never outlive their subscription.
      useChatStore.getState().dropProvisional(chatId);
    });
  };
}

export function isChatHeld(chatId: string): boolean {
  return holds.has(chatId);
}

async function start(chatId: string, generation: number, { worktreeId }: HoldOptions): Promise<void> {
  const current = () => holds.has(chatId) && generations.get(chatId) === generation;
  const store = useChatStore.getState();
  if (store.chats[chatId]) store.touch(chatId);
  if (!useChatStore.getState().chats[chatId]?.loaded) {
    const cached = await readCachedChat(chatId).catch(() => null);
    if (!current()) return;
    if (cached) useChatStore.getState().restore(chatId, cached);
  }
  if (!current()) return;
  if (worktreeId) localReleases.set(chatId, holdLocalChat(chatId, worktreeId));
  if (!useChatStore.getState().chats[chatId]?.loaded) {
    // Cold: the tail is the first paint, and the subscribe that follows starts
    // at its end. A failed tail leaves the cursor at 0 and the bounded replay
    // fills in instead.
    await loadChatTail(chatId);
    if (!current()) return;
  }
  useOrgSocket.getState().subscribeChat(chatId);
}
/** Chats whose cold tail failed to load, so the transcript can say so (with
 * a Retry) instead of showing a skeleton for as long as the relay is down. */
export const useChatTailFailures = create<{ failed: Record<string, true> }>(() => ({ failed: {} }));

function markTailFailed(chatId: string, failed: boolean) {
  useChatTailFailures.setState((state) => {
    if (Boolean(state.failed[chatId]) === failed) return state;
    const next = { ...state.failed };
    if (failed) next[chatId] = true;
    else delete next[chatId];
    return { failed: next };
  });
}

/** The newest turns of a chat into the store. Also the hover prefetch. */
export function loadChatTail(chatId: string): Promise<void> {
  const existing = inflightTail.get(chatId);
  if (existing) return existing;
  const request = client.chatEvents(chatId, { mode: "tail", tailTurns: TAIL_TURNS })
    .then((page) => {
      markTailFailed(chatId, false);
      useChatStore.getState().applyCovered(chatId, {
        // Each event checked on its own, like the socket's (web #3).
        events: readChatEvents(chatId, page.events).events,
        coverage: page.coverage,
        hasMoreBefore: page.hasMoreBefore,
        // The subscribe went out with it; if its replay got here first and
        // has not reached the tail yet, the replay finishes the job.
        onHole: "ignore",
      });
    })
    .catch(() => markTailFailed(chatId, true))
    .finally(() => inflightTail.delete(chatId));
  inflightTail.set(chatId, request);
  return request;
}

/** Warms a chat for an intended open (sidebar hover): memory, then this
 * browser's cache, then the REST tail. Never subscribes. */
export async function prefetchChat(chatId: string): Promise<void> {
  if (useChatStore.getState().chats[chatId]?.loaded) return;
  const cached = await readCachedChat(chatId).catch(() => null);
  if (useChatStore.getState().chats[chatId]?.loaded) return;
  if (cached) {
    useChatStore.getState().restore(chatId, cached);
    return;
  }
  await loadChatTail(chatId);
}

/** The next older page, on scroll-up. Resolves once it has landed. */
export function loadOlderHistory(chatId: string): Promise<void> {
  const existing = inflightOlder.get(chatId);
  if (existing) return existing;
  const stream = useChatStore.getState().chats[chatId];
  if (!stream?.hasMoreBefore || !stream.oldestLoadedSeq || stream.oldestLoadedSeq <= 1) return Promise.resolve();
  const request = client.chatEvents(chatId, { mode: "before", beforeSeq: stream.oldestLoadedSeq, turns: OLDER_PAGE_TURNS })
    .then((page) => {
      useChatStore.getState().applyCovered(chatId, {
        events: readChatEvents(chatId, page.events).events,
        coverage: page.coverage,
        hasMoreBefore: page.hasMoreBefore,
      });
    })
    .finally(() => inflightOlder.delete(chatId));
  inflightOlder.set(chatId, request);
  return request;
}

/**
 * A transcript that crashed while rendering: what this browser holds for it,
 * in memory and on disk, may be what crashed, so it is dropped and the chat
 * read again from the relay (web #8) rather than restored to crash again.
 */
export function reloadChatFromRelay(chatId: string): void {
  useChatStore.getState().reset(chatId);
  const kv = persistentStore();
  const userId = persistenceUserId();
  if (kv && userId) void kv.delete(`${userPrefix(userId)}chat:${chatId}`).catch(() => undefined);
  void loadChatTail(chatId).then(() => {
    if (isChatHeld(chatId)) useOrgSocket.getState().subscribeChat(chatId);
  });
}

/** Test seam. */
export function resetChatSubscriptions(): void {
  holds.clear();
  generations.clear();
  pendingRelease.clear();
  for (const release of localReleases.values()) release();
  localReleases.clear();
  inflightTail.clear();
  inflightOlder.clear();
  useChatTailFailures.setState({ failed: {} });
}
